/**
 * Product page store availability drawer.
 * Only ever lists stores that actually stock the selected variant, with the live
 * quantity — the per-store stock comes from the middleware
 * (GET {delivery_middleware_url}/api/store-availability?sku=). Until it answers the
 * list shows a loading row, and a failed request shows an error rather than the full
 * store list (which would read as "in stock everywhere").
 *
 * The lookup runs on page load rather than on open, so the trigger button can be removed
 * for products no store carries instead of opening onto "not available in any store".
 * Opening the drawer reuses that result.
 */
class StoreAvailability extends DrawerComponent {
  constructor() {
    super();
    this._onVariantChanged = this._onVariantChanged.bind(this);
    this._onAfterShow = this._onAfterShow.bind(this);
    this._onAfterHide = this._onAfterHide.bind(this);
  }

  get requiresBodyAppended() {
    return true;
  }

  connectedCallback() {
    super.connectedCallback();

    this.base = (FoxTheme.routes.delivery_proxy_url || '').replace(/\/+$/, '');
    this.sectionId = this.dataset.sectionId || '';
    this.stores = this._parseStores();
    this.labels = {
      inStock: this.dataset.inStock || 'In stock',
      outOfStock: this.dataset.outOfStock || 'Out of stock',
      qty: this.dataset.qty || '',
      hoursLabel: this.dataset.hoursLabel || 'Working hours:',
      empty: this.dataset.empty || '',
      loading: this.dataset.loading || '',
      error: this.dataset.error || '',
      noSku: this.dataset.noSku || '',
    };

    this.listEl = this.querySelector('[data-sam-list]');
    this.searchEl = this.querySelector('[data-sam-search]');

    this.variant = null;
    this.availability = {};
    // idle | loading | loaded | error | no-sku
    this.status = 'idle';
    this.filtered = [];
    this._fetchCtrl = null;
    // SKU the current `availability` belongs to, so opening the drawer can reuse the
    // lookup the page already made instead of asking the middleware again.
    this._loadedSku = null;

    this.searchEl?.addEventListener('input', () => this._filterList());
    this.addEventListener(this.events.handleAfterShow, this._onAfterShow);
    this.addEventListener(this.events.handleAfterHide, this._onAfterHide);

    if (!this._prefetched) {
      this._prefetched = true;
      this.variant = this._currentVariant();
      this._reload();
    }

    // product-info.js re-renders #StoreAvailabilityTrigger-* on every variant change, which
    // drops the click listener ModalComponent bound to the original button. Bind lazily:
    // any trigger for this drawer that has no listener yet gets one on its first click.
    this.controls.forEach((btn) => (btn.dataset.samBound = '1'));
    this._onDelegatedClick = (event) => {
      const btn = event.target.closest(`[aria-controls="${this.id}"]`);
      if (!btn || btn.dataset.samBound) return;
      btn.dataset.samBound = '1';
      btn.addEventListener('click', this.onButtonClick.bind(this), { signal: this.abortController.signal });
      event.preventDefault();
      if (this.open) this.hide();
      else this.show(btn);
    };
    document.addEventListener('click', this._onDelegatedClick);

    document.addEventListener('variant:changed', this._onVariantChanged);
    if (FoxTheme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
      this._pubsubToken = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        (event) => {
          const variant = event?.data?.variant;
          if (variant && String(event?.data?.sectionId) === String(this.sectionId)) {
            this._onVariantChanged({ detail: { variant } });
          }
        }
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('variant:changed', this._onVariantChanged);
    document.removeEventListener('click', this._onDelegatedClick);
    this.removeEventListener(this.events.handleAfterShow, this._onAfterShow);
    this.removeEventListener(this.events.handleAfterHide, this._onAfterHide);
    if (this._pubsubToken) FoxTheme.pubsub.unsubscribe(this._pubsubToken);
    this._abortFetch();
  }

  _parseStores() {
    const id = this.dataset.storesId;
    if (id) {
      const node = document.getElementById(id);
      if (node?.textContent) {
        try {
          return JSON.parse(node.textContent);
        } catch (e) {
          return [];
        }
      }
    }
    return [];
  }

  _productInfo() {
    return (
      document.querySelector(`product-info[data-section="${this.sectionId}"]`) ||
      document.querySelector('product-info')
    );
  }

  _currentVariant() {
    const node = this._productInfo();
    const raw = node?.querySelector('variant-selects [data-selected-variant]')?.textContent;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /**
   * The SKU to look up. A product with only the default variant has no
   * <variant-selects> to read, so fall back to the SKU Liquid seeded on the element.
   */
  _sku() {
    return (this.variant?.sku || this.dataset.defaultSku || '').trim();
  }

  _onVariantChanged(event) {
    const variant = event?.detail?.variant;
    if (!variant) return;
    this.variant = variant;
    // Reload even while closed: the trigger's visibility depends on the new variant.
    this._reload();
  }

  _onAfterShow() {
    this.variant = this._currentVariant();
    if (this.status === 'loaded' && this._loadedSku === this._sku()) {
      this._filterList();
    } else {
      this._reload();
    }
    window.setTimeout(() => this.searchEl?.focus(), 150);
  }

  _onAfterHide() {
    if (this.searchEl) this.searchEl.value = '';
    this._filterList();
  }

  _reload() {
    this.availability = {};
    this._loadedSku = null;
    this.status = 'loading';
    this._renderList();
    this._fetchAvailability();
  }

  /**
   * The button that opens this drawer. Liquid renders it, and product-info.js swaps its
   * contents on a variant change, so it's looked up fresh rather than cached.
   */
  _triggerEl() {
    return document.getElementById(`StoreAvailabilityTrigger-${this.sectionId}`);
  }

  _anyStoreStock() {
    return this.stores.some((store) => this._isAvailable(store.pickup_key) === true);
  }

  /**
   * Whether the selected variant can still be ordered. A variant Shopify has sold out (no
   * stock and a 'deny' policy) stays sold out no matter what the shops hold — its buy button
   * reads "Разпродадено" next to a back-in-stock signup, so a green badge would contradict it.
   */
  _variantSellable() {
    if (this.variant) return this.variant.available !== false;
    return this.dataset.defaultAvailable !== 'false';
  }

  /**
   * Physical-store stock lives in Zeron, not in Shopify's inventory, so a product can be
   * out of stock online and still sit on a shelf — roughly half of them are. Both the
   * trigger and the stock badge therefore have to wait for the middleware rather than key
   * off Shopify's count.
   */
  _applyAvailability() {
    this._syncTrigger();
    this._syncStockBadge();
    this._syncOfflineWording();
  }

  /**
   * The one case Liquid cannot word on its own: nothing left online, but a shop still has
   * it. "Няма наличност" alone reads like the end of the road, and the trigger below it
   * then has to say where the product actually is. Both labels are restored the moment
   * the case no longer holds — a variant change can take the product back in stock.
   */
  _syncOfflineWording() {
    const offline =
      this.status === 'loaded' && this._anyStoreStock() && !this._variantSellable();

    const label = document.querySelector(`#StockDelivery-${this.sectionId} .product__stock-delivery__label`);
    if (label) {
      if (offline) {
        if (!label.dataset.samOriginal) label.dataset.samOriginal = label.textContent;
        label.textContent = this.dataset.offlineStock || 'Няма наличност онлайн';
      } else if (label.dataset.samOriginal) {
        label.textContent = label.dataset.samOriginal;
        delete label.dataset.samOriginal;
      }
    }

    const trigger = this._triggerEl();
    const triggerLabel = trigger && trigger.querySelector('.product__store-availability-text strong');
    if (!triggerLabel) return;
    if (offline) {
      if (!triggerLabel.dataset.samOriginal) triggerLabel.dataset.samOriginal = triggerLabel.textContent;
      triggerLabel.textContent = this.dataset.offlineTrigger || 'Виж наличност във физически магазини';
    } else if (triggerLabel.dataset.samOriginal) {
      triggerLabel.textContent = triggerLabel.dataset.samOriginal;
      delete triggerLabel.dataset.samOriginal;
    }
  }

  /**
   * Removed only once the middleware has confirmed no store carries this variant. An error
   * leaves it in place, since a failed request shouldn't strip a feature that works.
   */
  _syncTrigger() {
    const trigger = this._triggerEl();
    if (!trigger) return;
    if (this.status === 'no-sku') {
      trigger.hidden = true;
      return;
    }
    if (this.status !== 'loaded') {
      trigger.hidden = false;
      return;
    }
    trigger.hidden = !this._anyStoreStock();
  }

  /**
   * Shop stock counts as "В наличност" even when the online warehouse is empty, so it also
   * settles the backorder case Liquid can't: the caveat note gives way to the badge. This
   * only ever promotes, and only for a variant that is still orderable — a variant already
   * rendered in stock is shippable whatever the shops hold. The "доставка до N часа" line
   * stays with Liquid, since stock sitting in a shop isn't what gets couriered.
   */
  _syncStockBadge() {
    if (this.status !== 'loaded' || !this._anyStoreStock() || !this._variantSellable()) return;
    const root = document.getElementById(`StockDelivery-${this.sectionId}`);
    if (!root) return;

    const note = root.querySelector('.product__stock-delivery__note');
    if (note) note.hidden = true;

    const stock = root.querySelector('.product__stock-delivery__stock');
    if (!stock) return;
    stock.hidden = false;
    if (stock.classList.contains('product__stock-delivery__stock--in-stock')) return;

    stock.classList.remove('product__stock-delivery__stock--out-of-stock');
    stock.classList.add('product__stock-delivery__stock--in-stock');

    const icon = stock.querySelector('.product__stock-delivery__icon');
    if (icon && root.dataset.iconInStock) icon.src = root.dataset.iconInStock;
    const label = stock.querySelector('.product__stock-delivery__label');
    if (label && root.dataset.labelInStock) label.textContent = root.dataset.labelInStock;
  }

  _abortFetch() {
    if (this._fetchCtrl) {
      this._fetchCtrl.abort();
      this._fetchCtrl = null;
    }
  }

  async _fetchAvailability() {
    const sku = this._sku();
    if (!sku) {
      this.status = 'no-sku';
      this._renderList();
      this._applyAvailability();
      return;
    }
    if (!this.base) {
      this.status = 'error';
      this._renderList();
      this._applyAvailability();
      return;
    }

    this._abortFetch();
    this._fetchCtrl = new AbortController();
    const url = `${this.base}/api/store-availability?sku=${encodeURIComponent(sku)}`;

    try {
      const res = await fetch(url, { signal: this._fetchCtrl.signal });
      if (!res.ok) throw new Error('http ' + res.status);
      const data = await res.json();
      const map = {};
      (data.stores || []).forEach((row) => {
        if (row && row.pickup_key) map[row.pickup_key] = row;
      });
      this.availability = map;
      this.status = 'loaded';
      this._loadedSku = sku;
    } catch (err) {
      if (err.name === 'AbortError') return;
      this.status = 'error';
    }
    this._filterList();
    this._applyAvailability();
  }

  _isAvailable(key) {
    if (this.status !== 'loaded') return null;
    const row = this.availability[key];
    if (!row) return null;
    if (typeof row.available === 'boolean') return row.available;
    if (row.qty != null) return Number(row.qty) > 0;
    return null;
  }

  _qty(key) {
    const row = this.availability[key];
    const qty = row && row.qty != null ? Number(row.qty) : null;
    return Number.isFinite(qty) && qty > 0 ? qty : null;
  }

  /** "В наличност" plus the live per-store quantity when the middleware reports one. */
  _badgeLabel(key) {
    const qty = this._qty(key);
    if (qty === null || !this.labels.qty) return this.labels.inStock;
    return `${this.labels.inStock} — ${this.labels.qty.replace('__QTY__', String(qty))}`;
  }

  _badgeHtml(key) {
    const available = this._isAvailable(key);
    if (available === null) return '';
    if (available) {
      return `<span class="store-avail-drawer__badge store-avail-drawer__badge--in">
        <svg class="store-avail-drawer__badge-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M19.1666 14.925C19.1833 15.55 19.0166 16.1416 18.7166 16.65C18.55 16.95 18.325 17.225 18.075 17.45C17.5 17.9833 16.7416 18.3083 15.9 18.3333C14.6833 18.3583 13.6083 17.7333 13.0166 16.775C12.7 16.2833 12.5083 15.6917 12.5 15.0667C12.475 14.0167 12.9416 13.0666 13.6916 12.4416C14.2583 11.975 14.975 11.6833 15.7583 11.6666C17.6 11.625 19.125 13.0833 19.1666 14.925Z" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M14.5332 15.025L15.3749 15.825L17.1165 14.1416" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M2.6416 6.20001L9.99992 10.4583L17.3082 6.22499" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 18.0084V10.45" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M18.0079 7.64169V12.3584C18.0079 12.4001 18.0079 12.4333 17.9995 12.475C17.4162 11.9667 16.6662 11.6667 15.8329 11.6667C15.0495 11.6667 14.3245 11.9417 13.7495 12.4C12.9829 13.0084 12.4995 13.95 12.4995 15C12.4995 15.625 12.6745 16.2167 12.9829 16.7167C13.0579 16.85 13.1495 16.975 13.2495 17.0917L11.7245 17.9334C10.7745 18.4667 9.22453 18.4667 8.27453 17.9334L3.82453 15.4667C2.8162 14.9084 1.99121 13.5084 1.99121 12.3584V7.64169C1.99121 6.49169 2.8162 5.09171 3.82453 4.53337L8.27453 2.06669C9.22453 1.53335 10.7745 1.53335 11.7245 2.06669L16.1745 4.53337C17.1829 5.09171 18.0079 6.49169 18.0079 7.64169Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        ${this._esc(this._badgeLabel(key))}
      </span>`;
    }
    return `<span class="store-avail-drawer__badge store-avail-drawer__badge--out">
      <svg class="store-avail-drawer__badge-icon" viewBox="0 0 20 20" aria-hidden="true"><path fill="currentColor" d="M5 5l10 10M15 5 5 15" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>
      ${this._esc(this.labels.outOfStock)}
    </span>`;
  }

  _filterList() {
    const q = (this.searchEl?.value || '').trim().toLowerCase();
    this.filtered = this.stores.filter((s) => {
      // Only stores that stock the selected variant are ever listed.
      if (this._isAvailable(s.pickup_key) !== true) return false;
      if (!q) return true;
      const hay = [s.name, s.address, s.city].join(' ').toLowerCase();
      return hay.includes(q);
    });
    this._renderList();
  }

  _fullAddress(store) {
    const parts = [store.address, store.city].filter(Boolean);
    return parts.join(', ');
  }

  _messageHtml(text, modifier) {
    return `<li class="store-avail-drawer__empty${modifier ? ' store-avail-drawer__empty--' + modifier : ''}">${this._esc(
      text
    )}</li>`;
  }

  _renderList() {
    if (!this.listEl) return;

    if (this.status === 'loading') {
      this.listEl.innerHTML = this._messageHtml(this.labels.loading, 'loading');
      return;
    }
    if (this.status === 'error') {
      this.listEl.innerHTML = this._messageHtml(this.labels.error, 'error');
      return;
    }
    if (this.status === 'no-sku') {
      this.listEl.innerHTML = this._messageHtml(this.labels.noSku, 'error');
      return;
    }
    // Availability loaded but no store stocks the selected variant.
    if (this.filtered.length === 0) {
      this.listEl.innerHTML = this._messageHtml(this.labels.empty, 'empty');
      return;
    }

    this.listEl.innerHTML = this.filtered
      .map((store) => {
        const address = this._fullAddress(store);
        const mapBtn = store.map_url
          ? `<a class="store-avail-drawer__map-btn" href="${this._esc(store.map_url)}" target="_blank" rel="noopener noreferrer" aria-label="${this._esc(store.name)}">
              <img src="https://cdn.shopify.com/s/files/1/1057/7975/7405/files/arrow-square-right.svg?v=1782395164" alt="" width="20" height="20" loading="lazy" class="store-avail-drawer__map-arrow" aria-hidden="true">
            </a>`
          : '';
        const addressHtml = address
          ? store.map_url
            ? `<a class="store-avail-drawer__address" href="${this._esc(store.map_url)}" target="_blank" rel="noopener noreferrer">${this._esc(address)}</a>`
            : `<span class="store-avail-drawer__address">${this._esc(address)}</span>`
          : '';
        const phoneHtml =
          store.phone && store.phone.trim()
            ? `<a class="store-avail-drawer__phone" href="tel:${this._esc(store.phone.replace(/\s/g, ''))}">
                <svg class="store-avail-drawer__phone-icon" viewBox="0 0 20 20" aria-hidden="true"><path fill="currentColor" d="M6.5 3h2l1 4-2 1.5a9 9 0 0 0 4 4L13 10l4 1v2a1.5 1.5 0 0 1-1.5 1.5A11.5 11.5 0 0 1 3 5.5 1.5 1.5 0 0 1 4.5 4z"/></svg>
                ${this._esc(store.phone)}
              </a>`
            : '';
        const hoursHtml = store.hours
          ? `<div class="store-avail-drawer__hours">
              <strong>${this._esc(this.labels.hoursLabel)}</strong>
              <span>${this._esc(store.hours)}</span>
            </div>`
          : '';

        return `<li class="store-avail-drawer__item">
          <div class="store-avail-drawer__item-head">
            <h3 class="store-avail-drawer__item-name">${this._esc(store.name)}</h3>
            ${mapBtn}
          </div>
          ${addressHtml}
          ${this._badgeHtml(store.pickup_key)}
          ${phoneHtml}
          ${hoursHtml}
        </li>`;
      })
      .join('');
  }

  _esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('store-availability')) {
  customElements.define('store-availability', StoreAvailability);
}
